<script setup>
import { ref, onBeforeMount, computed } from "vue";
import axios from "axios";
import NotificationToasts from "@vuesora/Components/NotificationToasts/NotificationToasts.vue";
import EmailForm from "./Login/EmailForm.vue";
import PasswordForm from "./Login/PasswordForm.vue";
import EmailConfirmation from "./Login/EmailConfirmation.vue";
import ResetForm from "./Login/ResetForm.vue";
import { brandUrl as brandLogos } from "@constants/brands";
import { useNotificationStore } from "@stores/notification";
import { useUserStore } from "@stores/user";

const userStore = useUserStore();
const notification = useNotificationStore();

const props = defineProps({
    redirectUrl: {
        type: String,
        default: "",
    },
    reseturl: {
        type: String,
    },
    usecsrftoken: {
        type: Boolean,
    },
    errors: {
        type: Array,
    },
    hassessionstatus: {
        type: Boolean,
        default: false,
    },
    sessionstatus: {
        type: String,
        default: "",
    },
    orderNowUrl: {
        type: String,
        default: "",
    },
});

const currentForm = ref("login-email");
const emailError = ref("");
const passwordError = ref("");
const emailInput = ref("");
const passwordInput = ref("");
const isLoading = ref(false);
const isPasswordVisible = ref(false);
const isResendButtonDisabled = ref(false);
const confirmationTitle = ref("");
const confirmationDescription = ref("");
const formConfirmationType = ref("");
const endpointUrl = ref("");

const changeCurrentForm = (val) => {
    currentForm.value = val;
    emailError.value = "";
    passwordError.value = "";
};

const handleEmailChange = (val) => {
    if (Math.abs(val.length - emailInput.value.length) > 0) {
        emailError.value = "";
    }
    emailInput.value = val;
};

const handlePasswordChange = (val) => {
    passwordInput.value = val;
};

const changeConfirmationScreen = (confirmationType) => {
    if (confirmationType === "reset") {
        confirmationTitle.value = "Password reset link sent!";
        confirmationDescription.value =
            "We’ve sent the link to reset your password";
        endpointUrl.value = props.reseturl;
        formConfirmationType.value = "reset";
        changeCurrentForm("email-confirm");
    } else if (confirmationType === "setup") {
        confirmationTitle.value = "You’re almost there!";
        confirmationDescription.value =
            "We’ve sent the link to complete your account";
        endpointUrl.value = "/user-management-system/login/send-setup-email";
        formConfirmationType.value = "email-confirm";
        changeCurrentForm("email-confirm");
    }
};

function deleteCookie(name) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

const handleButtonClick = () => {
    isLoading.value = true;
    localStorage.setItem("lastEmailUsed", emailInput.value);

    axios
        .post("/user-management-system/login", {
            email: emailInput.value,
            password: passwordInput.value,
            redirect_to:
                new URLSearchParams(window.location.search).get(
                    "redirect_to"
                ) ?? props.redirectUrl,
        })
        .then((response) => {
            if (response.status === 200) {
                deleteCookie(`hideOnboardingBanner_drumeo`);
                deleteCookie(`hideOnboardingBanner_guitareo`);
                deleteCookie(`hideOnboardingBanner_singeo`);
                deleteCookie(`hideOnboardingBanner_pianote`);

                const redirectTo = response.data.redirect_to;
                if (redirectTo) {
                    window.location.href = redirectTo;
                }
            }
        })
        .catch((e) => {
            isLoading.value = false;
            passwordError.value =
                "Incorrect password. Try again or reset it below.";
        });
};

const toggleSeePassword = () => {
    isPasswordVisible.value = !isPasswordVisible.value;
};

const isButtonDisabled = computed(() => {
    return !(
        emailInput &&
        passwordInput &&
        emailInput.value &&
        passwordInput.value &&
        !isLoading.value
    );
});

onBeforeMount(() => {
    if (localStorage.getItem("lastEmailUsed") && props.errors?.length) {
        emailInput.value = localStorage.getItem("lastEmailUsed");
    }

    let urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has("email")) {
        emailInput.value = urlParams.get("email");
    }
});

const showNotification = (payload) => {
    notification.push(payload);
};

const validateEmail = () => {
    //todo remove comment and put confirmation screen where it should
    emailError.value = "";

    axios
        .post("/user-management-system/login/check-email", {
            email: emailInput.value,
        })
        .then((response) => {
            if (response.data.is_setup) {
                changeCurrentForm("login-password");
            } else {
                changeConfirmationScreen("setup");
            }
        })
        .catch(() => {
            emailError.value =
                "We can't find your account. Click below to join!";
        });
};
</script>

<template>
    <div class="tw-z-0 tw-h-[100vh] tw-w-full tw-bg-[#000C17]">
        <NotificationToasts
            :icon="notification.icon"
            :text="notification.text"
            :isError="notification.isError"
            :isMembersArea="false"
        />
        <div
            class="tw-absolute tw-z-20 tw-flex tw-min-h-screen tw-w-full tw-flex-col tw-items-center tw-justify-center tw-px-[20px] tw-text-white"
        >
            <section
                v-if="
                    currentForm !== 'email-confirm' && currentForm !== 'setup'
                "
                id="logoContainer"
                class="tw-flex tw-w-full tw-max-w-[400px] tw-flex-col tw-items-center tw-border-b-[1px] tw-border-[#223F57] tw-pb-[40px] tw-text-center"
            >
                <img
                    class="tw-mb-[15px] tw-w-full tw-max-w-[130px] md:tw-max-w-[240px]"
                    src="https://d38h3dn806jqj1.cloudfront.net/logos/musora-white_new.svg"
                    alt="Musora Logo"
                />
                <p class="tw-text-[14px] tw-leading-[21px] tw-text-[#9EC0DC]">
                    The ultimate music lessons experience
                </p>
                <p
                    class="tw-mt-[4px] tw-flex tw-h-[18px] tw-w-full tw-justify-center"
                >
                    <img
                        class="tw-mr-[9px] tw-h-[15px] tw-self-start"
                        :src="brandLogos.drumeo"
                        alt="Drumeo Logo"
                    />
                    <img
                        class="tw-mr-[9px] tw-h-[15px] tw-self-center"
                        :src="brandLogos.pianote"
                        alt="Pianote Logo"
                    />
                    <img
                        class="tw-mr-[9px] tw-h-[16px] tw-self-end"
                        :src="brandLogos.guitareo"
                        alt="Guitareo Logo"
                    />
                    <img
                        class="tw-h-[16px] tw-self-end"
                        :src="brandLogos.singeo"
                        alt="Singeo Logo"
                    />
                </p>
            </section>

            <div class="tw-flex tw-w-full tw-flex-col tw-items-center">
                <EmailForm
                    v-if="currentForm === 'login-email'"
                    :emailError="emailError"
                    :hassessionstatus="hassessionstatus"
                    :sessionstatus="sessionstatus"
                    :emailInput="emailInput"
                    :userStore="userStore"
                    :isLoading="isLoading"
                    :orderNowUrl="orderNowUrl"
                    @email-change="handleEmailChange"
                    @validate-email="validateEmail"
                />
                <PasswordForm
                    v-if="currentForm === 'login-password'"
                    :emailInput="emailInput"
                    :passwordError="passwordError"
                    :hassessionstatus="hassessionstatus"
                    :sessionstatus="sessionstatus"
                    :userStore="userStore"
                    :isPasswordVisible="isPasswordVisible"
                    :isLoading="isLoading"
                    :isButtonDisabled="isButtonDisabled"
                    @password-change="handlePasswordChange"
                    @button-click="handleButtonClick"
                    @change-confirmation-screen="changeConfirmationScreen"
                    @toggle-password="toggleSeePassword"
                    @change-form="changeCurrentForm"
                />
                <EmailConfirmation
                    v-if="currentForm === 'email-confirm'"
                    :confirmationType="formConfirmationType"
                    :emailInput="emailInput"
                    :endpointUrl="endpointUrl"
                    :usecsrftoken="usecsrftoken"
                    :userStore="userStore"
                    :confirmationTitle="confirmationTitle"
                    :confirmationDescription="confirmationDescription"
                    @change-form="changeCurrentForm"
                    @show-notification="showNotification"
                />
            </div>
        </div>
        <div
            v-if="currentForm === 'login-email'"
            class="tw-absolute tw-bottom-0 tw-z-40 tw-w-full tw-items-center tw-justify-center tw-bg-[#00101D] tw-pb-[40px] tw-pt-[20px] tw-text-center md:tw-bg-transparent"
        >
            <a
                href="/contact"
                class="tw-text-[14px] tw-leading-[21px] tw-text-[#9EC0DC] md:tw-text-white"
                >Get support</a
            >
        </div>
    </div>
</template>

<style type="text/css">
.loginEmail-label,
.loginPassword-label {
    font-weight: 700;
    font-size: 16px;
}
</style>
